import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useState, useEffect } from 'react'
import { Link, useRoute, useLocation } from 'wouter'
import { ArrowLeft, Save, Eye, Sparkles, AlertCircle } from 'lucide-react'
import { useAuth } from '@/contexts/auth-context'
import { buildApiUrl } from '@/config/api'
import { FileUpload } from '@/components/file-upload'
import { SubscriptionModal } from '@/components/subscription-modal'
import toast from 'react-hot-toast'

interface Rfp {
  id: string
  title: string
  description: string
  requirements: string[]
  status: string
  category: string
  budgetMin: number
  budgetMax: number
  budgetCurrency: string
  deadline: string
  attachments: string[]
  user: {
    id: string
    name: string
    email: string
    company: string
  }
}

export function EditRfp() {
  const { user } = useAuth()
  const [, setLocation] = useLocation()
  const [, params] = useRoute('/edit-rfp/:id')
  const rfpId = params?.id
  
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [rfp, setRfp] = useState<Rfp | null>(null)
  const [error, setError] = useState<string | null>(null)
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    requirements: [''],
    budgetMin: '',
    budgetMax: '',
    budgetCurrency: 'USD',
    deadline: '',
    category: '',
  })

  const [attachments, setAttachments] = useState<any[]>([])
  const [subscriptionStatus, setSubscriptionStatus] = useState<{
    hasActiveSubscription: boolean
    subscriptionPlan: string
  } | null>(null)
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)

  // Fetch RFP data on component mount
  useEffect(() => {
    const fetchRfp = async () => {
      if (!rfpId) {
        setError('RFP ID is required')
        setIsLoading(false)
        return
      }

      try {
        const token = localStorage.getItem('token')
        if (!token) {
          setError('Authentication required')
          setIsLoading(false)
          return
        }

        const response = await fetch(buildApiUrl(`/api/rfps/${rfpId}`), {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        })

        if (response.ok) {
          const data = await response.json()
          const rfpData = data.data
          
          // Check if user owns this RFP
          if (rfpData.user.id !== user?.id) {
            setError('You do not have permission to edit this RFP')
            setIsLoading(false)
            return
          }

          setRfp(rfpData)
          
          // Populate form with existing data
          setFormData({
            title: rfpData.title,
            description: rfpData.description,
            requirements: rfpData.requirements.length > 0 ? rfpData.requirements : [''],
            budgetMin: rfpData.budgetMin.toString(),
            budgetMax: rfpData.budgetMax.toString(),
            budgetCurrency: rfpData.budgetCurrency,
            deadline: rfpData.deadline.split('T')[0], // Convert ISO date to YYYY-MM-DD
            category: rfpData.category,
          })

          // Initialize attachments if available
          if (rfpData.attachments) {
            const attachmentObjects = rfpData.attachments.map((key: string) => ({
              key,
              filename: key.split('/').pop() || key,
              canDelete: true
            }))
            setAttachments(attachmentObjects)
          }

          // Fetch subscription status for attachment access
          try {
            const subscriptionResponse = await fetch(buildApiUrl('/api/subscription/status'), {
              headers: { 'Authorization': `Bearer ${token}` },
            })
            if (subscriptionResponse.ok) {
              const subscriptionData = await subscriptionResponse.json()
              setSubscriptionStatus(subscriptionData.data)
            }
          } catch (error) {
            console.error('Error fetching subscription status:', error)
          }
        } else if (response.status === 404) {
          setError('RFP not found')
        } else {
          setError('Failed to load RFP')
        }
      } catch (error) {
        console.error('Error fetching RFP:', error)
        setError('Failed to load RFP')
      } finally {
        setIsLoading(false)
      }
    }

    fetchRfp()
  }, [rfpId, user?.id])

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const addRequirement = () => {
    setFormData(prev => ({
      ...prev,
      requirements: [...prev.requirements, '']
    }))
  }

  const removeRequirement = (index: number) => {
    setFormData(prev => ({
      ...prev,
      requirements: prev.requirements.filter((_, i) => i !== index)
    }))
  }

  const updateRequirement = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      requirements: prev.requirements.map((req, i) => i === index ? value : req)
    }))
  }

  const generateWithAI = async () => {
    setIsGenerating(true)
    // TODO: Implement AI generation
    setTimeout(() => {
      setIsGenerating(false)
    }, 2000)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      // Validate form data (matching backend validation)
      if (!formData.title.trim()) {
        toast.error('Title is required (minimum 1 character)')
        return
      }
      if (formData.description.trim().length < 10) {
        toast.error('Description must be at least 10 characters long')
        return
      }
      if (!formData.category.trim()) {
        toast.error('Category is required (minimum 1 character)')
        return
      }
      if (!formData.deadline) {
        toast.error('Deadline is required')
        return
      }
      if (!formData.budgetMin || parseFloat(formData.budgetMin) <= 0) {
        toast.error('Minimum budget must be greater than 0')
        return
      }
      if (!formData.budgetMax || parseFloat(formData.budgetMax) <= 0) {
        toast.error('Maximum budget must be greater than 0')
        return
      }
      if (parseFloat(formData.budgetMin) >= parseFloat(formData.budgetMax)) {
        toast.error('Maximum budget must be greater than minimum budget')
        return
      }
      if (formData.requirements.length === 0 || formData.requirements.every(req => !req.trim())) {
        toast.error('At least one requirement is needed (minimum 1 character each)')
        return
      }
      // Check each requirement has at least 1 character
      for (let i = 0; i < formData.requirements.length; i++) {
        if (!formData.requirements[i].trim()) {
          toast.error(`Requirement ${i + 1} must have at least 1 character`)
          return
        }
      }

      const token = localStorage.getItem('token')
      if (!token) {
        toast.error('Authentication required')
        return
      }

      // Prepare data for API
      const rfpData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        requirements: formData.requirements.filter(req => req.trim()),
        budget: {
          min: parseFloat(formData.budgetMin),
          max: parseFloat(formData.budgetMax),
          currency: formData.budgetCurrency,
        },
        deadline: formData.deadline,
        category: formData.category.trim(),
        attachments: attachments.map(att => att.key), // Include file attachment keys
      }

      const response = await fetch(buildApiUrl(`/api/rfps/${rfpId}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(rfpData),
      })

      const data = await response.json()

      if (data.success) {
        toast.success('RFP updated successfully!')
        setLocation(`/rfp/${rfpId}`) // Redirect to RFP detail
      } else {
        toast.error(data.error || 'Failed to update RFP')
      }
    } catch (error) {
      console.error('Error updating RFP:', error)
      toast.error('Failed to update RFP. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-8">
        <div className="flex items-center space-x-4">
          <Link href="/request-for-proposal-marketplace">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Loading RFP...</h1>
          </div>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-2 text-muted-foreground">Loading RFP details...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !rfp) {
    return (
      <div className="space-y-8">
        <div className="flex items-center space-x-4">
          <Link href="/request-for-proposal-marketplace">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Error</h1>
            <p className="text-muted-foreground">
              {error || 'Failed to load RFP'}
            </p>
          </div>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <p className="text-muted-foreground mb-4">
              {error || 'RFP not found'}
            </p>
            <Link href="/request-for-proposal-marketplace">
              <Button>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to RFPs
              </Button>
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link href={`/rfp/${rfpId}`}>
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Edit RFP</h1>
            <p className="text-muted-foreground">
              Update your Request for Proposal.
            </p>
          </div>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline">
            <Eye className="mr-2 h-4 w-4" />
            Preview
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            <Save className="mr-2 h-4 w-4" />
            {isSubmitting ? 'Updating...' : 'Update RFP'}
          </Button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
            <CardDescription>
              Update the essential details for your RFP.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Title</label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Enter RFP title"
                  className="flex-1 px-3 py-2 border border-input rounded-md bg-background"
                  required
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={generateWithAI}
                  disabled={isGenerating}
                >
                  <Sparkles className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Description</label>
              <div className="flex space-x-2">
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Describe your project requirements, goals, and expectations..."
                  rows={4}
                  className="flex-1 px-3 py-2 border border-input rounded-md bg-background resize-none"
                  required
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={generateWithAI}
                  disabled={isGenerating}
                >
                  <Sparkles className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Category</label>
              <select
                value={formData.category}
                onChange={(e) => handleInputChange('category', e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-md bg-background"
                required
              >
                <option value="">Select a category</option>
                <optgroup label="Technology">
                  <option value="Technology">Technology</option>
                  <option value="Software Development">Software Development</option>
                  <option value="Web Development">Web Development</option>
                  <option value="Mobile Development">Mobile Development</option>
                  <option value="AI/ML">AI/ML</option>
                  <option value="Cybersecurity">Cybersecurity</option>
                  <option value="Cloud Computing">Cloud Computing</option>
                  <option value="DevOps">DevOps</option>
                </optgroup>
                <optgroup label="Business">
                  <option value="Consulting">Consulting</option>
                  <option value="Strategy">Strategy</option>
                  <option value="Operations">Operations</option>
                  <option value="Finance">Finance</option>
                  <option value="Legal">Legal</option>
                  <option value="HR">HR</option>
                </optgroup>
                <optgroup label="Marketing">
                  <option value="Digital Marketing">Digital Marketing</option>
                  <option value="Content Marketing">Content Marketing</option>
                  <option value="SEO">SEO</option>
                  <option value="Social Media">Social Media</option>
                  <option value="Branding">Branding</option>
                  <option value="Advertising">Advertising</option>
                </optgroup>
                <optgroup label="Creative">
                  <option value="Design">Design</option>
                  <option value="Graphic Design">Graphic Design</option>
                  <option value="UI/UX Design">UI/UX Design</option>
                  <option value="Video Production">Video Production</option>
                  <option value="Photography">Photography</option>
                  <option value="Animation">Animation</option>
                </optgroup>
                <optgroup label="Other">
                  <option value="Research">Research</option>
                  <option value="Education">Education</option>
                  <option value="Healthcare">Healthcare</option>
                  <option value="Real Estate">Real Estate</option>
                  <option value="Manufacturing">Manufacturing</option>
                  <option value="Logistics">Logistics</option>
                </optgroup>
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Budget & Timeline */}
        <Card>
          <CardHeader>
            <CardTitle>Budget & Timeline</CardTitle>
            <CardDescription>
              Set your budget range and project deadline.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Minimum Budget</label>
                <input
                  type="number"
                  value={formData.budgetMin}
                  onChange={(e) => handleInputChange('budgetMin', e.target.value)}
                  placeholder="1000"
                  min="0"
                  step="100"
                  className="w-full px-3 py-2 border border-input rounded-md bg-background"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Maximum Budget</label>
                <input
                  type="number"
                  value={formData.budgetMax}
                  onChange={(e) => handleInputChange('budgetMax', e.target.value)}
                  placeholder="5000"
                  min="0"
                  step="100"
                  className="w-full px-3 py-2 border border-input rounded-md bg-background"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Currency</label>
                <select
                  value={formData.budgetCurrency}
                  onChange={(e) => handleInputChange('budgetCurrency', e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-md bg-background"
                >
                  <option value="USD">USD ($)</option>
                  <option value="EUR">EUR (€)</option>
                  <option value="GBP">GBP (£)</option>
                  <option value="CAD">CAD (C$)</option>
                  <option value="AUD">AUD (A$)</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Deadline</label>
              <input
                type="date"
                value={formData.deadline}
                onChange={(e) => handleInputChange('deadline', e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-md bg-background"
                required
              />
            </div>
          </CardContent>
        </Card>

        {/* Requirements */}
        <Card>
          <CardHeader>
            <CardTitle>Requirements</CardTitle>
            <CardDescription>
              List the specific requirements and deliverables expected from vendors.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {formData.requirements.map((requirement, index) => (
              <div key={index} className="flex space-x-2">
                <input
                  type="text"
                  value={requirement}
                  onChange={(e) => updateRequirement(index, e.target.value)}
                  placeholder={`Requirement ${index + 1}`}
                  className="flex-1 px-3 py-2 border border-input rounded-md bg-background"
                  required
                />
                {formData.requirements.length > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => removeRequirement(index)}
                  >
                    ×
                  </Button>
                )}
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              onClick={addRequirement}
              className="w-full"
            >
              + Add Requirement
            </Button>
          </CardContent>
        </Card>

        {/* File Attachments - PRO Feature */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <span>File Attachments</span>
              {subscriptionStatus?.subscriptionPlan !== 'PRO' && (
                <span className="ml-2 px-2 py-1 text-xs bg-amber-100 text-amber-800 rounded-full">
                  PRO
                </span>
              )}
            </CardTitle>
            <CardDescription>
              Upload supporting documents, specifications, or reference materials for your RFP.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {subscriptionStatus?.subscriptionPlan === 'PRO' ? (
              <FileUpload
                rfpId={rfpId || null}
                initialAttachments={attachments}
                onAttachmentsChange={(newAttachments) => setAttachments(newAttachments)}
              />
            ) : (
              <div className="text-center py-8 space-y-4">
                <div className="flex justify-center">
                  <div className="p-3 bg-amber-100 rounded-full">
                    <AlertCircle className="h-6 w-6 text-amber-600" />
                  </div>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 mb-1">
                    File Attachments Available with PRO
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Upgrade to PRO to attach supporting documents, specifications, and reference materials to your RFPs.
                  </p>
                  <Button onClick={() => setShowSubscriptionModal(true)}>
                    Upgrade to PRO
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </form>

      {/* Subscription Modal */}
      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        feature="attachments"
        currentPlan={subscriptionStatus?.subscriptionPlan as 'FREE' | 'PRO'}
        hasActiveSubscription={subscriptionStatus?.hasActiveSubscription || false}
      />
    </div>
  )
} 